import type {
  Event,
  Outcome as OutcomeType,
  Competitor as CompetitorType,
} from "@/hooks/firestore/betting/types";
import { differenceInMinutes, isSameDay } from "date-fns";
import { titleCase } from "title-case";
import type { Nullable } from "@/types/utilities";
import type { RacingEvent } from "@/hooks/firestore/models/racingEvent";
import type {
  ProcessedRacingCompetitors,
  ProcessedRacingEvent,
} from "@/hooks/firestore/betting/useRacingEvents";
import type {
  ProcessedEvent,
  ProcessedMarkets,
} from "@/hooks/firestore/betting/useEvent";

export const mapRaceToEvent = ({
  event,
  competitors,
  markets,
}: {
  event: ProcessedRacingEvent;
  competitors: ProcessedRacingCompetitors;
  markets: ProcessedEvent["markets"];
}) => {
  const mapRaceCompetitorToEventCompetitor = (): Record<
    string,
    CompetitorType
  > =>
    competitors.reduce(
      (acc, value) => {
        if (value) {
          acc[value.id] = {
            abbreviation: value.number.toString(),
            name: value.name,
            type: "RACER",
            number: Number(value.number),
            silksUrl: event?.silksUrl,
          };
        }
        return acc;
      },
      {} as Record<string, CompetitorType>,
    );

  const mapRaceOutcomeToEventOutcome = (
    outcomes: Record<string, ProcessedMarkets[number]["outcomes"][string]>,
    marketType: ProcessedMarkets[number]["marketType"],
    sport: RacingEvent["sport"],
  ) => {
    return Object.keys(outcomes).reduce(
      (acc, key) => {
        const value = outcomes[key];
        const competitor = competitors?.find(
          (x) => x.id === value.competitorId,
        );

        // Determine the starting position for the competitor.
        // If the sport is horse racing and the competitor's starting position is provided, format and include it.
        // Otherwise, leave the starting position as an empty string.
        const startingPosition =
          competitor?.startingPosition && sport !== "GREYHOUNDS"
            ? ` (${formatStartingPosition(competitor?.startingPosition)})`
            : ``;

        if (marketType === "CUSTOM") {
          acc[key] = {
            name: value.name,
            active: value.active,
            odds: Number(value.odds ?? 0),
            competitorId: value.competitorId || "",
            result: value.result,
            abbreviation: value?.name?.substring(0, 3),
            type: "CUSTOM",
          };
          return acc;
        }

        if (value) {
          acc[key] = {
            name: `${competitor?.number}. ${competitor?.name}${startingPosition}`,
            active: value.result != undefined,
            odds: Number(value.odds ?? 0),
            competitorId: value.competitorId || "",
            result: "",
            abbreviation: value?.name?.substring(0, 3),
            silksUrl: event?.silksUrl,
            number: Number(competitor?.number),
          };
        }

        return acc;
      },
      {} as Record<string, OutcomeType>,
    );
  };

  return {
    eventId: event.id,
    filters: event.filters,
    competitors: mapRaceCompetitorToEventCompetitor(),
    eventName: `${event.venue} - R${event.number}`,
    eventType: "RACE",
    parentEventName: event.venue,
    sport: event.sport,
    activeMarketCount: 0,
    attributes: {
      raceMeetingId: event?.meetingId,
    },
    scheduledStartTime: event.scheduledStartTime,
    nextBetStop: event.scheduledStartTime,
    status: event.status,
    hub: "racing",
    markets: markets.map((m) => ({
      id: m.id,
      bettingType: "Prematch",
      marketType: m.marketType,
      name: m.name,
      status: event.status,
      outcomes: mapRaceOutcomeToEventOutcome(
        m.outcomes,
        m.marketType,
        event.sport,
      ),
      sourceType: "race",
      specifiers: null,
      hasPromotionalMarkets: m.hasPromotionalMarkets,
      blockPromotionsBets: m.blockPromotionsBets,
    })),
    expectedResults: event.expectedResults,
    hasPromotionalMarkets: event.hasPromotionalMarkets,
  } satisfies Event;
};

export const formatStartingPosition = (startingPosition: string) => {
  if (!startingPosition || startingPosition === "0") {
    return "";
  }
  return `${startingPosition}`;
};

export const getTimezoneFrom2LetterCountryCode = (
  countryCode: string,
  stateCode: string,
) => {
  switch (countryCode) {
    case "AU":
      switch (stateCode) {
        case "NSW":
          return "Australia/Sydney";
        case "VIC":
          return "Australia/Melbourne";
        case "QLD":
          return "Australia/Brisbane";
        case "SA":
          return "Australia/Adelaide";
        case "WA":
          return "Australia/Perth";
        case "TAS":
          return "Australia/Hobart";
        case "NT":
          return "Australia/Darwin";
        default:
          return "Australia/Sydney";
      }
    case "NZ":
      return "Pacific/Auckland";
    case "GB":
    case "UK":
      return "Europe/London";
    case "IE":
      return "Europe/Dublin";
    case "ZA":
      return "Africa/Johannesburg";
    case "US":
      return "America/New_York";
    case "CA":
      return "America/Toronto";
    case "SG":
      return "Asia/Singapore";
    case "JP":
      return "Asia/Tokyo";
    case "KR":
      return "Asia/Seoul";
    case "HK":
      return "Asia/Hong_Kong";
    case "FR":
      return "Europe/Paris";
    case "ES":
      return "Europe/Madrid";
    case "BR":
      return "America/Sao_Paulo";
    case "AR":
      return "America/Argentina/Buenos_Aires";
    case "IN":
      return "Asia/Kolkata";
    default:
      return "Australia/Sydney";
  }
};

export const getIsSRMAvailable = ({
  scheduledStartTime,
  raceTimezone,
  sport,
  semAvailableAtMs,
  nowMs = Date.now(),
}: {
  scheduledStartTime?: Date;
  raceTimezone: Nullable<string>;
  sport: string;
  semAvailableAtMs?: number;
  nowMs?: number;
}) => {
  if (semAvailableAtMs != null) {
    return semAvailableAtMs < nowMs;
  } else {
    const now = new Date(nowMs);

    const currentHourInTimeZone = Number(
      now.toLocaleString("en-AU", {
        hour: "numeric",
        hour12: false,
        // A null tz throws; undefined falls back to the system zone.
        timeZone: raceTimezone ?? undefined,
      }),
    );

    if (
      ["GREYHOUNDS", "HARNESS_RACING"].includes(sport) &&
      differenceInMinutes(scheduledStartTime, now) < 20
    ) {
      return true;
    } else if (["GREYHOUNDS", "HARNESS_RACING"].includes(sport)) {
      return false;
    } else if (
      raceTimezone?.includes("Australia") &&
      (!isSameDay(scheduledStartTime, now) || currentHourInTimeZone < 9)
    ) {
      return false;
    } else if (
      !raceTimezone?.includes("Australia") &&
      differenceInMinutes(scheduledStartTime, now) > 60
    ) {
      return false;
    }

    return true;
  }
};

export const getJockeyLabel = (
  jockey?: string,
  sport?: RacingEvent["sport"],
) =>
  sport && jockey
    ? sport === "GREYHOUNDS"
      ? null
      : `${sport === "HARNESS_RACING" ? `D` : `J`}: ${jockey}`
    : "";

const isDomainName = (word: string): boolean => {
  return word.includes(".") && word.split(".").length > 1;
};

const isBenchMark = (word: string): boolean => {
  return /BM\d+/i.test(word);
};

export const toTitleCaseWithExclusions = (input: string): string => {
  const titleCased = input
    .split(" ")
    .map((word) => {
      // A&E etc..
      if (word.includes("&")) return word.toUpperCase();

      if (
        commonTwoLetterWords.includes(word.toLowerCase()) ||
        isDomainName(word)
      ) {
        return word.toLowerCase();
      }

      if (racingAcronyms.includes(word.toUpperCase()) || isBenchMark(word)) {
        return word.toUpperCase();
      }

      return titleCase(word.toLowerCase());
    })
    .join(" ");

  return titleCased;
};

const commonTwoLetterWords = [
  "if",
  "or",
  "an",
  "up",
  "at",
  "by",
  "in",
  "of",
  "on",
  "to",
  "as",
  "is",
  "am",
  "be",
  "do",
  "go",
  "he",
  "it",
  "me",
  "my",
  "no",
  "ok",
  "ow",
  "us",
  "we",
  "yo",
];

const racingAcronyms = [
  "EBF", // European Breeders Fund
  "MDN", // Maiden
  "HCP", // Handicap
  "G1",
  "G2",
  "G3", // Group 1, Group 2, Group 3 races
  "LR", // Listed Race
  "ALW", // Allowance
  "CLM", // Claiming
  "STK", // Stakes
  "GRD", // Graded
  "NW1",
  "NW2",
  "NW3", // Non-winners of one, two, three races other than maiden, claiming, or starter
  "TURF", // Turf course
  "DIRT", // Dirt course
  "AW", // All-weather
  "SPR", // Sprint
  "RT", // Route
  "MD", // Middle Distance
  "LD", // Long Distance
  "XXXX", // four X sponsored meeeting, we still wish to retain casing
];

export const getRaceUrl = (
  type: "race" | "meeting",
  meetingId: string,
  raceId?: string,
): string =>
  type === "meeting"
    ? `/racing/betting/meeting/${meetingId}`
    : `/racing/betting/race/${meetingId}/${raceId}`;

export const getIconByWeather = (weather: string) => {
  if (["fine", "clear"].some((match) => weather.includes(match))) {
    return "weatherFine";
  }

  if (weather.includes("heavy")) return "weatherHeavy";

  if (["rain", "showers", "shwry"].some((match) => weather.includes(match))) {
    return "weatherShowers";
  }

  if (
    ["clouds", "cloudy", "overcast", "ocast"].some((match) =>
      weather.includes(match),
    )
  ) {
    return "weatherOvercast";
  }

  return null;
};
